import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X, ChevronRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LangToggle } from "@/context/I18nContext";

const links = [
  { to: "/", label: "Home" },
  { to: "/ai-scan", label: "AI Scan" },
  { to: "/services", label: "Services" },
  { to: "/protection", label: "Protection" },
  { to: "/pricing", label: "Pricing" },
  { to: "/for-business", label: "Commercial" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${scrolled ? "glass" : "bg-transparent"}`} data-testid="main-navbar">
      <nav className="max-w-[1500px] mx-auto px-5 lg:px-10 h-[68px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <img src="/errah-logo.png" alt="ERRAH" className="h-9 w-auto object-contain" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="font-serif text-xl font-semibold tracking-[0.28em]">ERRAH</span>
            <span className="text-[8px] tracking-[0.3em] text-[#C9A45C] mt-0.5">PROTECT WHAT MATTERS</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => {
            const active = pathname === l.to;
            return (
              <Link key={l.label} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`relative text-[13px] tracking-wide transition-colors ${active ? "text-white" : "text-gray-400 hover:text-white"}`}>
                {l.label}
                {active && <span className="absolute -bottom-1.5 left-0 right-0 h-px bg-[#C9A45C]" />}
              </Link>
            );
          })}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <LangToggle />
          {user && <Link to={user.role === "admin" ? "/admin" : user.role === "partner" ? "/partner/dashboard" : "/my-errah"} className="text-[13px] text-gray-300 hover:text-white transition-colors">Account</Link>}
          <button onClick={() => navigate("/consultation")} data-testid="nav-consult-cta"
            className="bg-[#C9A45C] text-black px-5 py-2.5 text-[12px] font-semibold tracking-wide hover:bg-[#E4C987] hover:-translate-y-0.5 transition-all rounded-sm">
            BOOK CONSULTATION
          </button>
        </div>

        <button className="lg:hidden text-white" onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-[#080808] border-t border-[#C9A45C]/15 px-5 py-4" data-testid="mobile-menu">
          {links.map((l) => (
            <Link key={l.label} to={l.to} onClick={() => setOpen(false)}
              className="flex items-center justify-between py-3 border-b border-white/5 text-gray-300">
              {l.label} <ChevronRight size={16} className="text-gray-600" />
            </Link>
          ))}
          <button onClick={() => { setOpen(false); navigate("/consultation"); }}
            className="mt-4 w-full bg-[#C9A45C] text-black py-3 text-sm font-semibold tracking-wide rounded-sm">
            BOOK CONSULTATION
          </button>
        </div>
      )}
    </header>
  );
}
